import {
    Component,
    AfterViewInit,
    ChangeDetectorRef,
    HostListener,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { confirm } from 'devextreme/ui/dialog';
import {
    QuoteParameter,
    Item,
} from 'src/app/shared/services/print-sheet/interfaces/quote-param';
import moment from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import {
    debounce as _debounce,
    DebouncedFunc,
    each as _each,
    map as _map,
    toNumber as _toNumber,
    includes as _includes,
    toString as _toString,
    reduce as _reduce,
    isFinite as _isFinite,
    get as _get,
    isString as _isString,
} from 'lodash';
import {
    ThAddressSuggestorService,
    PrintSheetService,
    RenderPersistReportService,
    NotificationService,
    UtilService,
    MessageType,
} from 'src/app/shared/services';
import { FormatNumberPipe } from 'src/app/shared/pipes';
import {
    entityById,
    toSellerPrintBlock,
} from 'src/app/shared/config/entity-config';
import { splitVatInclusive, vatRateAt } from 'src/app/shared/config/vat';
import { MessageService } from './services/message.service';
import {
    getInitialQuoteInput,
    inputMomentFormat,
    ProductInfo,
    QuoteInput,
} from './models/quote-input';
import { deliveryCourierList, deliveryTermList } from './models/input-choices';
import { branchList } from 'src/app/shared/config/branch-list';
import { RenderRequest } from 'src/app/shared/services/print-sheet/interfaces/report-api-request';
import { environment } from 'src/environments/environment';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { DxBoxComponent } from 'devextreme-angular/ui/box';
import {
    DxiItemComponent,
    DxoPagingComponent,
    DxoEditingComponent,
    DxoRowDraggingComponent,
    DxiColumnComponent,
} from 'devextreme-angular/ui/nested';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxDateBoxComponent } from 'devextreme-angular/ui/date-box';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { NgClass } from '@angular/common';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxAutocompleteComponent } from 'devextreme-angular/ui/autocomplete';
import { DxTextAreaComponent } from 'devextreme-angular/ui/text-area';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { FormatNumberPipe as FormatNumberPipe_1 } from '../../shared/pipes/format-number.pipe';

@Component({
    selector: 'app-quote-generator',
    templateUrl: './quote-generator.component.html',
    styleUrls: ['./quote-generator.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxLoadPanelComponent,
        DxBoxComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxTextBoxComponent,
        DxDateBoxComponent,
        DxSwitchComponent,
        NgClass,
        DxSelectBoxComponent,
        DxAutocompleteComponent,
        DxTextAreaComponent,
        DxDataGridComponent,
        DxoPagingComponent,
        DxoEditingComponent,
        DxoRowDraggingComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        DxNumberBoxComponent,
        FormatNumberPipe_1,
    ],
})
export class QuoteGeneratorComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private thAddressSuggestor = inject(ThAddressSuggestorService);
    private formatNumberPipe = inject(FormatNumberPipe);
    private printSheetService = inject(PrintSheetService);
    private renderPersistService = inject(RenderPersistReportService);
    private messageService = inject(MessageService);
    private utilService = inject(UtilService);
    private notificationService = inject(NotificationService);

    branchList = branchList;
    deliveryCourierList = deliveryCourierList;
    deliveryTermList = deliveryTermList;

    postCodeSuggestions: any[] = [];
    provinceSuggestions: any[] = [];
    districtSuggestions: any[] = [];
    subDistrictSuggestions: any[] = [];

    shareData: {
        fileName: string;
        sharedUrl: string;
    } | null = null;
    isQuoteExporting = false;
    requestData!: string;
    messageSourceWindow: Window | null = null;

    inputValues: QuoteInput = getInitialQuoteInput();
    onInputsUpdated: DebouncedFunc<() => void>;

    private previousSuggestionSelection = '';

    // The shop-brand block for non-VAT quotes. Deliberately carries NO tax id:
    // a quotation without VAT needs no seller tax registration, and the id
    // this block used to print was the questionable shared individual id
    // (CONSOLE-BUILD-SPEC P1). The quote template omits the row when empty.
    private sellerInfo = {
        name: 'GenLogic Shop',
        website: 'https://www.genlogic.co.th/',
        address:
            '111/12 ซ.โชคชัยร่วมมิตร แขวงจอมพล เขตุจตุจักร กรุงเทพมหานคร 10900',
        taxId: '',
        email: 'sale@genlogic.co.th',
    };

    // VAT quotes are the company quoting; the identity comes from the entity
    // registry rather than a third hand-typed copy. (This fixed the previously
    // divergent address bytes — the old literal misspelled เขตจตุจักร.)
    private sellerInfoVat = toSellerPrintBlock(entityById('genlogic-co'));

    constructor() {
        this.onInputsUpdated = _debounce(this.internalOnInputsUpdated, 200);
        this.onReorder = this.onReorder.bind(this);
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: any): void {
        const responseStatus: string = _get(event, 'data.status');
        if (responseStatus === 'ready') {
            this.messageSourceWindow = event.source;
        } else if (responseStatus === 'data' && event.data.data) {
            this.messageService.fillInputFromMessage(
                event.data.data,
                this.inputValues,
            );
            this.notificationService.pushMessage({
                message: 'Lnwshop data is loaded',
                messageType: MessageType.success,
            });
        } else if (responseStatus === 'error') {
            this.notificationService.pushMessage({
                message: `Lnwshop Data loading failed with responsed error: ${event.data.message}`,
                messageType: MessageType.error,
            });
        }
    }

    requestMessage(): void {
        if (this.messageSourceWindow) {
            console.log('posted message');
            this.messageSourceWindow.postMessage(
                {
                    status: 'request',
                },
                '*',
            );
        } else {
            console.error('No connection');
        }
    }

    ngAfterViewInit(): void {
        this.postCodeSuggestions = this.thAddressSuggestor.getAllZipCodes();
        this.provinceSuggestions = this.thAddressSuggestor.getAllProvinces();
        this.fillInitialValues(this.inputValues);
        this.cdref.detectChanges();
        // DEBUG Message
        // this.messageService.fillInputFromMessage(a, this.inputValues);
    }

    clearForm(): void {
        confirm('Filled data will be reset.', 'Confirm Clear Data ?').then(
            (result: boolean) => {
                if (result) {
                    this.inputValues = getInitialQuoteInput();
                    this.fillInitialValues(this.inputValues);
                    this.notificationService.pushMessage({
                        message: 'Form is cleared.',
                        messageType: MessageType.info,
                    });
                }
            },
        );
    }

    exportPdf(): void {
        if (!this.isQuoteExporting) {
            this.internalOnInputsUpdated();
            this.warnIfShippingTermsEmpty();
            const request = this.constructQuoteRequest();
            let tabTitle = 'ใบเสนอราคา';
            if (request.quoteInfo.quoteNo) {
                tabTitle += ` ${request.quoteInfo.quoteNo}`;
            }
            this.isQuoteExporting = true;
            this.printSheetService.printQuote([request], tabTitle).subscribe(
                () => {
                    this.notificationService.pushMessage({
                        message: 'Quote is successfully exported.',
                        messageType: MessageType.success,
                    });
                },
                (error) => {
                    console.error(error);
                    this.notificationService.pushMessage({
                        message: this.utilService.errorToString(error),
                        messageType: MessageType.error,
                    });
                },
                () => {
                    this.isQuoteExporting = false;
                },
            );
        }
    }

    async saveAndShare(): Promise<void> {
        if (!this.isQuoteExporting) {
            this.internalOnInputsUpdated();
            if (this.isShippingTermsEmpty()) {
                // Saving publishes the document, so unlike exportPdf() the
                // omission is a question, not a toast: Yes continues, No stops.
                const proceed = await confirm(
                    'ยังไม่ได้เลือก "การจัดส่งสินค้า" — เอกสารจะไม่แสดงเงื่อนไขการจัดส่ง<br>ต้องการดำเนินการต่อหรือไม่?',
                    'ยังไม่ได้เลือกการจัดส่งสินค้า',
                );
                if (!proceed) {
                    return;
                }
            }
            this.shareData = null;
            const request = this.constructQuoteRequest();
            const payload: RenderRequest<QuoteParameter> = {
                data: { pages: [request] },
                template: {
                    name: environment.quoteTemplateName,
                },
            };
            this.isQuoteExporting = true;
            this.renderPersistService
                .renderAndGetSharedUrl<QuoteParameter>({
                    renderRequest: payload,
                    reportStoreRequest: {
                        fileName: `${request.quoteInfo.quoteNo}`
                            ? `${
                                  this.inputValues.quoteInfo.billingNote
                                      ? 'I'
                                      : 'Q'
                              }${request.quoteInfo.quoteNo}.pdf`
                            : 'untitled.pdf',
                        parents: ['1Yjy5wsCR4AOgh4QpmkGcY_YJXOL3xHNa'],
                    },
                })
                .subscribe(
                    (response) => {
                        this.shareData = {
                            fileName: response.name,
                            sharedUrl: response.webViewLink,
                        };
                        this.notificationService.pushMessage({
                            message: `${response.name} shared URL is now available.`,
                            messageType: MessageType.success,
                        });
                        this.isQuoteExporting = false;
                    },
                    (error) => {
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                        this.isQuoteExporting = false;
                    },
                );
        }
    }

    // การจัดส่งสินค้า starts EMPTY by design (2026-08-19) so the operator
    // picks the delivery term per quote — this is the matching guard: surface
    // the omission when the document is generated, without blocking it.
    // Billing notes hide the shipping section entirely, so they never warn.
    private isShippingTermsEmpty(): boolean {
        return (
            !this.inputValues.quoteInfo.billingNote &&
            !this.formatString(this.inputValues.quoteInfo.shippingTerms)
        );
    }

    private warnIfShippingTermsEmpty(): void {
        if (this.isShippingTermsEmpty()) {
            this.notificationService.pushMessage({
                message:
                    'ยังไม่ได้เลือก "การจัดส่งสินค้า" — เอกสารจะไม่แสดงเงื่อนไขการจัดส่ง',
                messageType: MessageType.warning,
            });
        }
    }

    openSharedUrl(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            window.open(sharedUrl, '_blank');
        }
    }

    copySharedUrlToClibboard(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            navigator.clipboard.writeText(sharedUrl).then(
                () => {
                    this.notificationService.pushMessage({
                        message: 'URL copied to clipboard.',
                        messageType: MessageType.success,
                        displayTime: 100,
                    });
                },
                (e) => {
                    console.error(e);
                },
            );
        }
    }

    calculateProductsAmount(rowData: ProductInfo): number {
        const unitPrice = _get(rowData, 'unitPrice');
        const quantity = _get(rowData, 'quantity');
        if (_isFinite(unitPrice) && _isFinite(quantity)) {
            return rowData.unitPrice * rowData.quantity;
        }
        return 0;
    }

    onRemarkAutoFillButtonClicked(e: any): void {
        let textContent: string = _get(e, 'element.textContent');
        if (textContent) {
            textContent = textContent.substring(2);
            if (textContent) {
                if (!_isString(this.inputValues.quoteInfo.remark)) {
                    this.inputValues.quoteInfo.remark = '';
                }
                this.inputValues.quoteInfo.remark =
                    this.inputValues.quoteInfo.remark.trim();
                if (this.inputValues.quoteInfo.remark) {
                    this.inputValues.quoteInfo.remark += ', ';
                }
                this.inputValues.quoteInfo.remark += textContent;
            }
        }
    }

    onSaved(): void {
        this.generateProductOrderNumber();
        this.onInputsUpdated();
    }

    onReorder(e: any): void {
        const visibleRows = e.component.getVisibleRows();
        const toIndex = this.inputValues.productItems.indexOf(
            visibleRows[e.toIndex].data,
        );

        const fromIndex = this.inputValues.productItems.indexOf(e.itemData);

        this.inputValues.productItems.splice(fromIndex, 1);
        this.inputValues.productItems.splice(toIndex, 0, e.itemData);
        this.generateProductOrderNumber();
    }

    private fillInitialValues(input: QuoteInput): void {
        input.quoteInfo.date = moment().format(inputMomentFormat);
        // shippingTerms deliberately starts EMPTY (2026-08-19): the operator
        // picks the delivery term per quote; a silent default was mis-quoting
        // lead times.
        input.quoteInfo.isSigned = true;
        this.shareData = null;
    }

    private formatDisplayQuoteDate(input: string): string {
        if (input) {
            return formatBuddhistEra(
                moment(input, inputMomentFormat),
                'DD MMMM YYYY',
            );
        }
        return '';
    }

    private formatString(input: any): string {
        return _toString(input).trim();
    }

    private formatDecimal(input: any, decimalPoint: number): string {
        const inputStr = _toString(input).trim();
        if (inputStr) {
            return this.formatNumberPipe.transform(inputStr, decimalPoint);
        }
        return inputStr;
    }

    private constructQuoteRequest(): QuoteParameter {
        const totalProductPrice: number =
            _toNumber(this.inputValues.quoteInfo.totalProductPrice) || 0;
        const shippingCost: number =
            _toNumber(this.inputValues.quoteInfo.shippingCost) || 0;
        const requestParam: QuoteParameter = {
            sellerInfo: this.inputValues.quoteInfo.isVat
                ? this.sellerInfoVat
                : this.sellerInfo,
            quoteInfo: {
                billingNote: this.inputValues.quoteInfo.billingNote
                    ? 'true'
                    : '',
                quoteNo: this.formatString(this.inputValues.quoteInfo.quoteNo),
                date: this.formatDisplayQuoteDate(
                    this.inputValues.quoteInfo.date,
                ),
                validUntil: this.formatDisplayQuoteDate(
                    this.inputValues.quoteInfo.validUntil,
                ),
                customTitle: this.formatString(
                    this.inputValues.quoteInfo.customTitle,
                ),
                remark: this.formatString(this.inputValues.quoteInfo.remark),
                deliveryCourier: this.formatString(
                    this.inputValues.quoteInfo.shippingCarrier,
                ),
                deliveryTerms: this.formatString(
                    this.inputValues.quoteInfo.shippingTerms,
                ),
                hidePaymentMethod: this.inputValues.quoteInfo.hidePaymentMethod
                    ? 'true'
                    : '',
                hideSignatureFields: this.inputValues.quoteInfo
                    .hideSignatureFields
                    ? 'true'
                    : '',
                signed: this.inputValues.quoteInfo.isSigned ? 'true' : '',
                tableTotal: this.formatDecimal(
                    totalProductPrice + shippingCost,
                    2,
                ),
                vat: this.inputValues.quoteInfo.isVat
                    ? this.formatDecimal(
                          this.inputValues.quoteInfo.vatAmount || 0,
                          2,
                      )
                    : '',
                priceIncludesVat:
                    this.inputValues.quoteInfo.isVat &&
                    this.inputValues.quoteInfo.priceIncludesVat
                        ? 'true'
                        : '',
                discount: this.inputValues.quoteInfo.discount
                    ? this.formatDecimal(this.inputValues.quoteInfo.discount, 2)
                    : '',
                shippingCost: shippingCost
                    ? this.formatDecimal(
                          this.inputValues.quoteInfo.shippingCost || 0,
                          2,
                      )
                    : '',
                netPrice: this.formatDecimal(
                    this.inputValues.quoteInfo.netPrice || 0,
                    2,
                ),
            },
            customerInfo: {
                name: this.formatString(this.inputValues.customerInfo.name),
                address: this.formatString(
                    this.inputValues.customerInfo.useCustomAddress
                        ? this.inputValues.customerInfo.customAddress
                        : this.inputValues.customerInfo.fullAddress,
                ),
                taxId: this.formatString(this.inputValues.customerInfo.taxId),
                branch: this.formatString(this.inputValues.customerInfo.branch),
            },
            items: _map<ProductInfo, Item>(
                this.inputValues.productItems,
                (item: ProductInfo): Item => {
                    return {
                        itemNo: this.formatDecimal(item.itemNo, 0),
                        description: this.formatString(item.description),
                        unitPrice: this.formatDecimal(item.unitPrice, 2),
                        quantity: this.formatDecimal(item.quantity, 0),
                        amount: this.formatDecimal(item.amount, 2),
                    };
                },
            ),
        };
        return requestParam;
    }

    private internalOnInputsUpdated(): void {
        this.suggestAddressIfNeeded();
        this.constructFullAddress();
        this.calculatePrices();
        this.cdref.detectChanges();
    }

    private calculatePrices() {
        _each(this.inputValues.productItems, (prduct: ProductInfo) => {
            if (_isFinite(prduct.unitPrice) && _isFinite(prduct.quantity)) {
                prduct.amount = prduct.unitPrice * prduct.quantity;
            } else {
                prduct.amount = 0;
            }
        });
        const productPrice: number = _reduce(
            this.inputValues.productItems,
            (total: number, item) => {
                const amountNum = _toNumber(item.amount);
                if (_isFinite(amountNum)) {
                    return total + item.amount;
                }
                return total;
            },
            0,
        );
        this.inputValues.quoteInfo.totalProductPrice = productPrice;

        const shippingCost =
            _toNumber(this.inputValues.quoteInfo.shippingCost) || 0;
        const discount = _toNumber(this.inputValues.quoteInfo.discount) || 0;

        const tableAfterDiscount: number =
            productPrice + shippingCost - discount;

        let vatAmount = 0;
        let netPrice = tableAfterDiscount;
        if (this.inputValues.quoteInfo.isVat) {
            if (this.inputValues.quoteInfo.priceIncludesVat) {
                // The entered prices already carry VAT: the sum IS the net,
                // and the VAT line is backed out of it (same split as the
                // tax-invoice form's รวมใน mode).
                vatAmount = splitVatInclusive(tableAfterDiscount).output;
            } else {
                // Add-on-top, the default — a quotation normally states
                // price + VAT.
                vatAmount = _toNumber(tableAfterDiscount) * vatRateAt() || 0;
                vatAmount =
                    Math.round((vatAmount + Number.EPSILON) * 100) / 100;
                netPrice = tableAfterDiscount + vatAmount;
            }
        }
        this.inputValues.quoteInfo.vatAmount = vatAmount;
        this.inputValues.quoteInfo.netPrice = netPrice;
    }

    private suggestAddressIfNeeded(): void {
        const selectedPostCode =
            _toString(this.inputValues.customerInfo.postCode) || '';
        const selectedProvince = this.inputValues.customerInfo.province || '';
        const selectedDistrict = this.inputValues.customerInfo.district || '';

        const currentSuggestionSelection =
            selectedPostCode + selectedProvince + selectedDistrict;

        if (this.previousSuggestionSelection !== currentSuggestionSelection) {
            this.previousSuggestionSelection = currentSuggestionSelection;
            if (selectedPostCode.length === 5) {
                const selectedPostCodeNum = _toNumber(selectedPostCode);
                const province: string =
                    this.thAddressSuggestor.getProvinceByPostCode(
                        selectedPostCodeNum,
                    );
                this.provinceSuggestions = [province];

                if (selectedProvince) {
                    const districts: string[] =
                        this.thAddressSuggestor.getDistrictsByPostCode(
                            selectedPostCodeNum,
                        );

                    this.districtSuggestions = districts;
                } else {
                    this.districtSuggestions = [];
                }

                if (selectedDistrict) {
                    const subDistricts: string[] =
                        this.thAddressSuggestor.getSubDistrictsByDistrict(
                            selectedPostCodeNum,
                            selectedDistrict,
                        );

                    this.subDistrictSuggestions = subDistricts;
                } else {
                    this.subDistrictSuggestions = [];
                }
            } else {
                this.provinceSuggestions =
                    this.thAddressSuggestor.getAllProvinces();
            }
        }
    }

    private constructFullAddress(): void {
        const address1 = this.inputValues.customerInfo.address1?.trim();
        const subDistrict = this.inputValues.customerInfo.subDistrict?.trim();
        const district = this.inputValues.customerInfo.district?.trim();
        const province = this.inputValues.customerInfo.province?.trim();
        const postCode = _toString(
            this.inputValues.customerInfo.postCode,
        ).trim();
        let result = '';
        if (address1) {
            result += address1;
        }
        const isBangkok = _includes(province, 'กรุงเทพ');
        if (subDistrict) {
            result += isBangkok ? ' แขวง' : ' ตำบล';
            result += subDistrict;
        }
        if (district) {
            result += isBangkok ? ' เขต' : ' อำเภอ';
            result += district;
        }
        if (province) {
            result += ` จังหวัด${province}`;
        }
        if (postCode) {
            result += ` ${postCode}`;
        }
        result = result.trim();
        this.inputValues.customerInfo.fullAddress = result;
    }

    private generateProductOrderNumber(): void {
        _each(this.inputValues.productItems, (item, index) => {
            item.itemNo = index + 1;
        });
    }
}
